import React, { useCallback, useEffect, useState } from 'react';
import { IconButton } from '@material-ui/core';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import styles from '../constants/styles';
import {
  clearPaginationInfo,
  getAllEntitiesCatalog as getAllCategoriesCatalog,
  getEntities as getCategories,
  setPageAction as setPage,
  setRowsPerPageAction as setRowsPerPage,
} from '../../../redux/actions/categoriesActions';
import EditIcon from '@material-ui/icons/Edit';
import AddBoxIcon from '@material-ui/icons/AddBox';
import TableView from '../../TableView/TableView';
import ColumnInterface from '../../../interfaces/column';
import RBISearchButton from '../../RBISearchButton/RBISearchButton';
import RBISelectButton from '../../RBISelectButton/RBISelectButton';
import CreateCategory from './CategoryCreate';
import CategoryEdit from './CategoryEdit';
import { actionListener, cleanEmptyString } from '../../../utils/helpers';
import store from '../../../redux/store';
import {
  CATEGORIES_CREATE_CATEGORY,
  CATEGORIES_EDIT_CATEGORY,
} from '../../../redux/action-types/categories-types';
import { useTranslation } from 'react-i18next';
import AppConstants from '../../../AppConstants';

export default function CategoryTab() {
  const classes = styles();
  const dispatch: any = useDispatch();
  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const categories = useSelector(
    (state: RootStateOrAny) => state.CategoriesReducer.entities,
  );
  const entitiesFilter = useSelector(
    (state: RootStateOrAny) => state.CategoriesReducer.entitiesCatalog,
  );
  const paginationConfig = useSelector(
    (state: RootStateOrAny) => state.CategoriesReducer.paginationConfig,
  );
  const [openCreateCategory, setOpenCreateCategory] = useState(false);
  const [openCategoryEdit, setOpenCategoryEdit] = useState(false);
  const [categoriesTreated, setCategoriesTreated] = useState([]);
  const [category, setCategory] = useState({});

  const handleModalCategoryEdit =
    (value, idCategory = 0) =>
    () => {
      if (idCategory) {
        setCategory(categories.find(c => c.id === idCategory));
      }
      setOpenCategoryEdit(value);
    };

  useEffect(() => {
    setCategoriesTreated(
      categories.map(c => ({
        ...c,
        status: c.is_active
          ? categoryTabStrings.active
          : categoryTabStrings.inactive,
        action: (
          <IconButton onClick={handleModalCategoryEdit(true, c.id)}>
            <EditIcon />
          </IconButton>
        ),
      })),
    );
  }, [categories]);

  const [searchObject, setSearchObject] = useState<any>({
    name: '',
    isActive: '',
  });
  const [searchObjectPrev, setSearchObjectPrev] = useState<any>({
    name: '',
    isActive: '',
  });

  const refreshOnSave = () => {
    dispatch(
      getCategories({
        ...cleanEmptyString(searchObject),
        brand: selectedBrand,
        page: paginationConfig.page,
        limit: paginationConfig.rowsPerPage,
        paginated: 1,
        name: searchObject?.name?.name,
      }),
    );
    dispatch(getAllCategoriesCatalog({ brand: selectedBrand }));
  };

  useEffect(() => {
    const unsubscribeCategoryEdit = actionListener(
      store,
      CATEGORIES_EDIT_CATEGORY.SUCCESS,
      function (action) {
        refreshOnSave();
      },
    );
    const unsubscribeCategoryCreate = actionListener(
      store,
      CATEGORIES_CREATE_CATEGORY.SUCCESS,
      function (action) {
        refreshOnSave();
      },
    );
    return function () {
      unsubscribeCategoryEdit();
      unsubscribeCategoryCreate();
    };
  }, [
    paginationConfig.page,
    paginationConfig.rowsPerPage,
    searchObject.isActive,
    searchObject.name,
  ]);
  useEffect(() => {
    if (selectedBrand) {
      dispatch(clearPaginationInfo());
      dispatch(
        getCategories({
          brand: selectedBrand,
          page: paginationConfig.page,
          limit: AppConstants.rowsPerPage,
          paginated: 1,
          name: searchObject?.name?.name,
        }),
      );
    }
  }, [selectedBrand]);

  const handleApplyButton = () => {
    setSearchObject({ ...searchObjectPrev });
    dispatch(setPage(1));
    dispatch(
      getCategories({
        page: 1,
        limit: paginationConfig.rowsPerPage,
        brand: selectedBrand,
        ...cleanEmptyString(searchObjectPrev),
        name: searchObjectPrev?.name?.name,
        paginated: 1,
      }),
    );
  };

  const handleModalCreateCategory = value => () => {
    setOpenCreateCategory(value);
  };
  const handleChangeAutocomplete = (name: string, value: any) => {
    setSearchObjectPrev({
      ...searchObject,
      [name]: value ? value : '',
    });
    if (!value) {
      setSearchObject({
        ...searchObject,
        [name]: value ? value : '',
      });
    }
  };
  const [nameInput, setNameInput] = useState<string>('');
  const [timer, setTimer] = useState<any>(null);

  const callBackFunctionModels = useCallback(
    value => {
      clearTimeout(timer);
      if (nameInput !== value && (value.length > 2 || value === '')) {
        setNameInput(value);
        setTimer(setTimeout(() => {}, 1000));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timer, setNameInput, setTimer],
  );
  const handleInputChangeAutocomplete = (name: string, value: any) => {
    if (name === 'name') {
      callBackFunctionModels(value);
    }
  };

  const handleChangeSelect = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const name = event.target.name as keyof typeof searchObject;
    setSearchObjectPrev({
      ...searchObject,
      [name]: event.target.value,
    });
  };

  const { t } = useTranslation();

  const statusOptions = [
    { name: t('app.common.All'), value: '' },
    { name: t('app.common.active'), value: 1 },
    { name: t('app.common.inactive'), value: 0 },
  ];

  const categoryTabStrings = {
    id: t('createModel.id'),
    status: t('createModel.status'),
    category_name: t('createModel.categoryName'),
    active: t('createModel.active'),
    inactive: t('createModel.inactive'),
    not_categories_found: t('createModel.noCategoriesFound'),
  };

  const columns: ColumnInterface[] = [
    {
      id: 'id',
      label: categoryTabStrings.id,
      align: 'left',
    },
    {
      id: 'name',
      label: (
        <RBISearchButton
          id={'name'}
          valueOption={searchObjectPrev['name']}
          labelText={categoryTabStrings.category_name}
          onChange={handleChangeAutocomplete}
          onChangeInput={handleInputChangeAutocomplete}
          handleApplyButton={handleApplyButton}
          options={entitiesFilter}
        />
      ),
      align: 'left',
    },
    {
      id: 'status',
      label: (
        <RBISelectButton
          id={'isActive'}
          valueOption={searchObjectPrev['isActive']}
          labelText={categoryTabStrings.status}
          onChangeInput={handleChangeSelect}
          width={100}
          options={statusOptions}
          handleApplyButton={handleApplyButton}
        />
      ),
      align: 'left',
    },
    {
      id: 'action',
      label: (
        <IconButton onClick={handleModalCreateCategory(true)}>
          <AddBoxIcon />
        </IconButton>
      ),
    },
  ];

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(1));
    dispatch(
      getCategories({
        ...cleanEmptyString(searchObject),
        page: 1,
        limit: +event.target.value,
        brand: selectedBrand,
        paginated: 1,
        name: searchObject?.name?.name,
      }),
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(
      getCategories({
        ...cleanEmptyString(searchObject),
        page: newPage + 1,
        limit: paginationConfig.rowsPerPage,
        brand: selectedBrand,
        paginated: 1,
        name: searchObject?.name?.name,
      }),
    );
  };

  return (
    <div className={classes.root}>
      <div>
        {openCreateCategory && (
          <CreateCategory
            open={openCreateCategory}
            handleClose={handleModalCreateCategory(false)}
          />
        )}
        {openCategoryEdit && (
          <CategoryEdit
            open={openCategoryEdit}
            handleClose={handleModalCategoryEdit(false)}
            categoryToEdit={category}
          />
        )}
      </div>
      <div>
        <TableView
          columns={columns}
          rows={categoriesTreated}
          paginationConfig={paginationConfig}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          messageEmptyArray={categoryTabStrings.not_categories_found}
        />
      </div>
    </div>
  );
}
